// Conversion event helpers. Each helper no-ops if the pixel isn't loaded
// (ad blocker, network failure, bootstrap not yet run) — never throws.

const GA4_ID = "G-YN1HNRBX3S";

const GADS = {
  SignUp: {
    send_to: "AW-987138275/mtUTCKiHrqwcEOOR2tYD",
    value: 10,
    currency: "EUR",
  },
  Lead: {
    send_to: "AW-987138275/x42-CImnrqwcEOOR2tYD",
    value: 10,
    currency: "EUR",
  },
  AddToCart: {
    send_to: "AW-987138275/uoX9COzplawcEOOR2tYD",
    value: 10,
    currency: "EUR",
  },
  Purchase: {
    send_to: "AW-987138275/AJVUCPOclawcEOOR2tYD",
    value: 10,
    currency: "EUR",
  },
};

export function metaTrack(event, params) {
  if (typeof window === "undefined") return;
  const fbq = window.fbq;
  if (typeof fbq !== "function") return;
  if (params) fbq("track", event, params);
  else fbq("track", event);
}

export function metaTrackCustom(event, params) {
  if (typeof window === "undefined") return;
  const fbq = window.fbq;
  if (typeof fbq !== "function") return;
  if (params) fbq("trackCustom", event, params);
  else fbq("trackCustom", event);
}

const LINKEDIN = {
  InitiateCheckout: 28081009,
  Lead: 28081017,
  Contact: 28081025,
  SubmitApplication: 28081033,
  AddToCart: 28081049,
  Purchase: 28081057,
  SignUp: 28081097,
};

export function linkedinTrack(eventName) {
  if (typeof window === "undefined") return;
  const lintrk = window.lintrk;
  if (typeof lintrk !== "function") return;
  const id = eventName && LINKEDIN[eventName];
  if (id) lintrk("track", { conversion_id: id });
  else lintrk("track");
}

export function googleAdsTrack(event, opts) {
  if (typeof window === "undefined") return;
  const gtag = window.gtag;
  if (typeof gtag !== "function") return;
  const c = GADS[event];
  if (!c) return;
  const params = {
    send_to: c.send_to,
    value: opts && opts.value != null ? opts.value : c.value,
    currency: c.currency,
  };
  if (opts && opts.transactionId) {
    params.transaction_id = String(opts.transactionId);
  }
  gtag("event", "conversion", params);
}

// Reddit uses a fixed set of standard event names directly (no per-event
// numeric conversion IDs like LinkedIn). Map the app's semantic event names
// onto Reddit's standard events; anything unmapped fires as a Custom event.
const REDDIT = {
  SignUp: "SignUp",
  Lead: "Lead",
  AddToCart: "AddToCart",
  InitiateCheckout: "AddToCart",
  Purchase: "Purchase",
  ViewContent: "ViewContent",
  Search: "Search",
};

const REDDIT_PIXEL_ID = "a2_gorczop03fx0";

// Advanced matching: re-init the pixel with hashed match keys once the user is
// known (Reddit hashes email client-side). init does not auto-fire PageVisit,
// so re-calling it purely to attach match keys is safe. Mirrors how the Meta
// pixel passes `em` at signup.
export function redditIdentify({ email, externalId } = {}) {
  if (typeof window === "undefined") return;
  const rdt = window.rdt;
  if (typeof rdt !== "function") return;
  const keys = {};
  if (email) keys.email = String(email).toLowerCase().trim();
  if (externalId != null) keys.externalId = String(externalId);
  if (!keys.email && !keys.externalId) return;
  rdt("init", REDDIT_PIXEL_ID, keys);
}

export function redditTrack(event, params) {
  if (typeof window === "undefined") return;
  const rdt = window.rdt;
  if (typeof rdt !== "function") return;
  const mapped = event && REDDIT[event];
  if (mapped) {
    if (params) rdt("track", mapped, params);
    else rdt("track", mapped);
  } else {
    // No standard equivalent — send as Custom, tagged with the original name.
    rdt("track", "Custom", { customEventName: event, ...(params || {}) });
  }
}

// GA4 event helper. Pins send_to to the GA4 measurement ID so events
// route to GA4 only and don't accidentally trigger Google Ads conversions.
export function gaTrack(eventName, params) {
  if (typeof window === "undefined") return;
  const gtag = window.gtag;
  if (typeof gtag !== "function") return;
  gtag("event", eventName, { send_to: GA4_ID, ...(params || {}) });
}

export function gaSelectContent(contentType, contentId) {
  gaTrack("select_content", { content_type: contentType, content_id: contentId });
}

// Auth and onboarding flows redirect via window.location.href, which can
// cancel in-flight pixel beacons. Fire-then-redirect with a small delay
// to give the beacons time to leave the browser.
export function flushAndRedirect(url, delayMs = 250) {
  if (typeof window === "undefined") return;
  setTimeout(() => {
    window.location.href = url;
  }, delayMs);
}

/**
 * Landing-page view for a redeem campaign. Builds "viewed but didn't redeem"
 * retargeting audiences on Meta + Reddit. (Per-route PageView/PageVisit already
 * fires for all platforms; LinkedIn/Google remarketing is covered by that.)
 * @param {{ campaignId?: string|number }} [opts]
 */
export function trackRedeemView({ campaignId } = {}) {
  const params =
    campaignId != null
      ? { content_type: "product", content_ids: [String(campaignId)] }
      : undefined;
  metaTrack("ViewContent", params);
  redditTrack("ViewContent", params);
}

/**
 * Redeem completed (success screen shown). Fires a Lead conversion to all four
 * ad platforms, with Reddit advanced matching when the recipient email is known.
 * @param {{ email?: string, campaignId?: string|number }} [opts]
 */
export function trackRedeemSuccess({ email, campaignId } = {}) {
  if (email) redditIdentify({ email }); // advanced matching first
  metaTrack("Lead", email ? { em: String(email).toLowerCase().trim() } : undefined);
  linkedinTrack("Lead"); // conversion_id 28081017
  googleAdsTrack("Lead", campaignId != null ? { transactionId: campaignId } : undefined);
  redditTrack("Lead");
}

// ---------- Quote funnel ----------
// The app's buying flow ends in "Request quote", so the retargeting stages are
// Add to Quote → view quote/cart → quote requested. Every entry point goes
// through these helpers so all platforms see the same events. `source` is sent
// to GA4 as `funnel_source` to tell the entry points apart.

// GA4 fallback value for a lead when the quote total is unknown.
const DEFAULT_LEAD_VALUE = 10;

/**
 * Item added to the quote/cart. Call only after the API confirmed the add.
 * @param {{ sku?: string|number, name?: string, quantity?: number, unitPrice?: number,
 *   variant?: string, transactionId?: string, source?: string }} opts
 */
export function trackAddToQuote({ sku, name, quantity = 1, unitPrice, variant, transactionId, source } = {}) {
  const price = Number(unitPrice) > 0 ? Number(unitPrice) : undefined;
  const value = price ? price * quantity : undefined;
  metaTrack("AddToCart", {
    content_ids: sku != null ? [sku] : undefined,
    content_type: "product",
    content_name: name,
    value,
    currency: "EUR",
  });
  linkedinTrack("AddToCart");
  googleAdsTrack("AddToCart", { value, transactionId });
  redditTrack("AddToCart", { value, currency: "EUR", itemCount: quantity });
  gaTrack("add_to_cart", {
    currency: "EUR",
    value,
    funnel_source: source,
    items: [{
      item_id: sku,
      item_name: name,
      item_variant: variant,
      quantity,
      price,
    }],
  });
}

/**
 * Quote/cart page opened with items in it.
 * @param {{ value?: number, items?: Array<object>, source?: string }} opts
 */
export function trackViewQuote({ value, items = [], source } = {}) {
  metaTrackCustom("ViewQuote", { value, currency: "EUR", num_items: items.length });
  gaTrack("view_cart", { currency: "EUR", value, funnel_source: source, items });
}

/**
 * Quote request created. Call only in the API success branch, never on edits.
 * Submitting a quote is the end of the buying flow in this app, so GA4 gets the
 * full ecommerce tail here: begin_checkout + purchase (keyed by the quote id so
 * GA4 dedupes retries) in addition to generate_lead.
 * @param {{ quoteId?: string|number, value?: number, items?: Array<object>,
 *   contentIds?: Array<string|number>, user?: { id?: any, email?: string }, source?: string,
 *   beginCheckoutFired?: boolean }} opts — set beginCheckoutFired when the caller
 *   already sent begin_checkout on page open (CheckoutPage) so it isn't doubled.
 */
export function trackQuoteRequested({ quoteId, value, items = [], contentIds, user, source, beginCheckoutFired = false } = {}) {
  const dedupId = `quote_${quoteId ?? user?.id ?? "anon"}`;
  const amount = Number(value) > 0 ? Number(value) : undefined;
  if (!beginCheckoutFired) {
    gaTrack("begin_checkout", {
      currency: "EUR",
      value: amount,
      funnel_source: source,
      items,
    });
  }
  gaTrack("purchase", {
    currency: "EUR",
    value: amount,
    transaction_id: dedupId,
    funnel_source: source,
    items,
  });
  metaTrack("Lead", {
    content_name: "quote_request",
    value: amount,
    currency: "EUR",
    content_ids: contentIds,
    num_items: items.length || undefined,
  });
  linkedinTrack("Lead");
  googleAdsTrack("Purchase", {
    value: 1,
    transactionId: quoteId != null ? dedupId : `${dedupId}_${Date.now()}`,
  });
  gaTrack("generate_lead", {
    currency: "EUR",
    value: amount ?? DEFAULT_LEAD_VALUE,
    transaction_id: dedupId,
    funnel_source: source,
    items,
  });
  if (user) redditIdentify({ email: user.email, externalId: user.id });
  redditTrack("Lead", {
    value: amount,
    currency: "EUR",
    itemCount: items.length || undefined,
    transactionId: dedupId,
  });
}
